// The saved profit breakdown for one closed deal, opened from the Reports
// Profit column and edited there with Lead History's editor — the same loader
// and payload, so a deal opened and saved here goes back as it came.
//
// The rows are not carried in the reports payload — most are never opened, and
// one that is should be current — so the opportunity is fetched on click.
// `onSaved` refreshes Reports: the count, its colour and the totals are the
// backend's, so only a refetch can move them.
import React, { useEffect, useState } from 'react'
import Popover from '@mui/material/Popover'
import { toast } from 'react-toastify'
import fetcher from '../../../fetcher'
import { EditableProfitPopover } from '../PersonDetailPage/EditableProfitPopover'

export const ReportsProfitPopover = ({ opportunityId, anchorEl, onClose, onSaved }) => {
    // The last response, and which opportunity it answers for.
    const [loaded, setLoaded] = useState({ id: null, opportunity: null })

    useEffect(() => {
        // Forgotten on every change, closing included: a slow fetch must never
        // put the row clicked before this one on screen, and reopening a deal
        // after a save must fetch it again rather than show what it held.
        setLoaded((prev) => (prev.id === null ? prev : { ...prev, id: null }))
        if (!opportunityId) return
        let stale = false
        fetcher.getCrmOpportunity(opportunityId)
            .then((res) => {
                if (!stale) setLoaded({ id: opportunityId, opportunity: res?.data || null })
            })
            .catch(() => {
                if (!stale) setLoaded({ id: opportunityId, opportunity: null })
            })
        // Clicking quickly between rows leaves the earlier response in flight.
        return () => { stale = true }
    }, [opportunityId])

    const answered = loaded.id !== null && loaded.id === opportunityId
    const ready = answered && Boolean(loaded.opportunity)

    // Writes to the opportunity the rows came from. False keeps the editor open
    // with the edits.
    const save = async (payload) => {
        try {
            await fetcher.updateCrmOpportunity(loaded.id, payload)
        } catch (e) {
            toast.error('Could not save that change.')
            return false
        }
        onSaved?.()
        return true
    }

    return (
        <>
            {anchorEl && !ready && (
                <Popover
                    open
                    anchorEl={anchorEl}
                    onClose={onClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    PaperProps={{ className: 'profit-breakdown-popover' }}
                >
                    <div className="pb-empty">{answered ? 'Could not load this breakdown.' : 'Loading…'}</div>
                </Popover>
            )}
            {/* Keeps the last opportunity while it closes, so the grid fades out whole. */}
            <EditableProfitPopover
                opportunity={loaded.opportunity}
                anchorEl={ready ? anchorEl : null}
                onClose={onClose}
                onSave={save}
            />
        </>
    )
}
